import { AlertTriangle, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ContentRevisionDetail } from '../api/content';
import {
  diffRevision,
  diffStats,
  type DiffLine,
  type DiffSkip,
  type FieldChange,
} from '../pages/contentRevisions.logic';

const LINE_CLASSES: Record<DiffLine['kind'], string> = {
  same: 'text-gray-text',
  add: 'bg-green-500/10 text-green-300',
  remove: 'bg-red-500/10 text-red-300',
};

const LINE_MARKS: Record<DiffLine['kind'], string> = { same: ' ', add: '+', remove: '-' };

/**
 * Unified, single-column diff. Lines wrap instead of scrolling sideways, so
 * a long legal paragraph stays readable on a phone, which is where approvals
 * actually get decided.
 */
function LineDiff({ lines }: { lines: ReadonlyArray<DiffLine | DiffSkip> }) {
  return (
    <div className="rounded-lg border border-gray-border bg-dark-lighter text-xs font-mono max-h-[60vh] overflow-y-auto">
      {lines.map((line, index) =>
        line.kind === 'skip' ? (
          <div
            key={index}
            className="px-3 py-1 text-gray-text/70 border-y border-gray-border/40 select-none"
          >
            … {line.count} unchanged line{line.count === 1 ? '' : 's'}
          </div>
        ) : (
          <div key={index} className={`flex gap-2 px-3 py-0.5 ${LINE_CLASSES[line.kind]}`}>
            <span className="select-none flex-shrink-0 w-3">{LINE_MARKS[line.kind]}</span>
            <span className="whitespace-pre-wrap break-words min-w-0">{line.text || ' '}</span>
          </div>
        )
      )}
    </div>
  );
}

function ValueBlock({ mark, text, tone }: { mark: string; text: string; tone: 'add' | 'remove' }) {
  return (
    <div className={`flex gap-2 px-3 py-1 rounded text-xs font-mono ${LINE_CLASSES[tone]}`}>
      <span className="select-none flex-shrink-0 w-3">{mark}</span>
      <span className="whitespace-pre-wrap break-words min-w-0">{text}</span>
    </div>
  );
}

function Change({ change }: { change: FieldChange }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-mono text-white break-all">
        {change.path}
        <span className="text-gray-text"> · {change.kind}</span>
      </p>
      {change.lines ? (
        <LineDiff lines={change.lines} />
      ) : (
        <div className="space-y-1">
          {change.before !== undefined && (
            <ValueBlock mark="-" text={change.before} tone="remove" />
          )}
          {change.after !== undefined && <ValueBlock mark="+" text={change.after} tone="add" />}
        </div>
      )}
    </div>
  );
}

export interface RevisionDiffProps {
  detail: ContentRevisionDetail | undefined;
  isLoading: boolean;
  error: unknown;
}

/**
 * DM-7214: what the second operator approves. The proposed content against
 * the live item, from the server, never a label and a note.
 */
export default function RevisionDiff({ detail, isLoading, error }: RevisionDiffProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-3 text-sm text-gray-text py-2">
        <RefreshCw className="w-4 h-4 animate-spin text-primary" />
        <span>Loading the proposed content…</span>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          {error instanceof Error ? error.message : 'The proposed revision could not be loaded.'}
        </span>
      </div>
    );
  }

  const { revision, base, stale } = detail;
  const changes = diffRevision(base, revision.proposal);
  const stats = diffStats(changes);

  return (
    <div className="space-y-3">
      {stale && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            The live route is now content v{base.version}; this revision was written against v
            {revision.base_version}. The diff below is against what is live now.
          </span>
        </div>
      )}

      <div className="rounded-lg border border-gray-border divide-y divide-gray-border/50 text-sm">
        <div className="px-3 py-2 flex gap-3">
          <span className="text-xs text-gray-text font-mono w-24 flex-shrink-0">based on</span>
          <span className="text-white">content v{revision.base_version}</span>
        </div>
        <div className="px-3 py-2 flex gap-3">
          <span className="text-xs text-gray-text font-mono w-24 flex-shrink-0">proposed</span>
          <span className="text-white">
            {formatDistanceToNow(new Date(revision.created_at), { addSuffix: true })} by{' '}
            <span className="font-mono text-xs break-all">{revision.author_id}</span>
          </span>
        </div>
        <div className="px-3 py-2 flex gap-3">
          <span className="text-xs text-gray-text font-mono w-24 flex-shrink-0">content hash</span>
          <span className="text-white font-mono text-xs break-all" title={revision.hash}>
            {revision.hash.slice(0, 16)}…
          </span>
        </div>
        {revision.change_note && (
          <div className="px-3 py-2 flex gap-3">
            <span className="text-xs text-gray-text font-mono w-24 flex-shrink-0">change note</span>
            <span className="text-white break-words whitespace-pre-wrap">
              {revision.change_note}
            </span>
          </div>
        )}
      </div>

      {changes.length === 0 ? (
        <p className="text-sm text-yellow-400">
          No differences: this revision is identical to what is live.
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-text">
            {changes.length} changed field{changes.length === 1 ? '' : 's'}
            {stats.added + stats.removed > 0 && (
              <>
                {' · '}
                <span className="text-green-400">+{stats.added}</span>{' '}
                <span className="text-red-400">-{stats.removed}</span> lines
              </>
            )}
          </p>
          <div className="space-y-4">
            {changes.map(change => (
              <Change key={change.path} change={change} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
